const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
  };
}

class ApiClient {
  private token: string | null = null;
  private refreshToken: string | null = null;
  private refreshPromise: Promise<boolean> | null = null;
  private onLogout: (() => void) | null = null;
  private onTokenRefreshed: ((token: string, refreshToken: string) => void) | null = null;

  constructor() {
    // Restore tokens from persisted auth store on module load
    // This prevents 401 race conditions where queries fire before React effects
    try {
      const stored = localStorage.getItem('auth-storage');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.state?.token) {
          this.token = parsed.state.token;
        }
        if (parsed.state?.refreshToken) {
          this.refreshToken = parsed.state.refreshToken;
        }
      }
    } catch {
      // Ignore parse errors
    }
  }

  setToken(token: string | null) {
    this.token = token;
  }

  setRefreshToken(refreshToken: string | null) {
    this.refreshToken = refreshToken;
  }

  setOnLogout(callback: () => void) {
    this.onLogout = callback;
  }

  setOnTokenRefreshed(callback: (token: string, refreshToken: string) => void) {
    this.onTokenRefreshed = callback;
  }

  private async attemptTokenRefresh(): Promise<boolean> {
    // Deduplicate concurrent refresh attempts
    if (this.refreshPromise) return this.refreshPromise;

    this.refreshPromise = (async () => {
      if (!this.refreshToken) return false;

      try {
        const response = await fetch(`${API_BASE}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: this.refreshToken }),
        });

        const json = await response.json();
        if (!response.ok || !json.success) return false;

        const { accessToken, refreshToken: newRefreshToken } = json.data.tokens;
        this.token = accessToken;
        this.refreshToken = newRefreshToken;

        // Sync Zustand store so React effects don't overwrite with stale token
        this.onTokenRefreshed?.(accessToken, newRefreshToken);

        return true;
      } catch {
        return false;
      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  async request<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
    const { method = 'GET', body, headers = {} } = options;

    const requestHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      ...headers,
    };

    if (this.token) {
      requestHeaders['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      method,
      headers: requestHeaders,
      body: body ? JSON.stringify(body) : undefined,
    });

    // On 401, attempt token refresh and retry once
    if (response.status === 401 && this.refreshToken && !endpoint.includes('/auth/refresh')) {
      const refreshed = await this.attemptTokenRefresh();
      if (refreshed) {
        // Retry with new token
        const retryHeaders: Record<string, string> = {
          'Content-Type': 'application/json',
          ...headers,
          Authorization: `Bearer ${this.token}`,
        };
        const retryResponse = await fetch(`${API_BASE}${endpoint}`, {
          method,
          headers: retryHeaders,
          body: body ? JSON.stringify(body) : undefined,
        });
        const retryJson: ApiResponse<T> = await retryResponse.json();
        if (!retryResponse.ok || !retryJson.success) {
          throw new Error(retryJson.error?.message || 'Request failed');
        }
        return retryJson.data as T;
      }
      // Refresh failed — trigger logout
      this.onLogout?.();
      throw new Error('Session expired. Please log in again.');
    }

    const json: ApiResponse<T> = await response.json();

    if (!response.ok || !json.success) {
      throw new Error(json.error?.message || 'Request failed');
    }

    return json.data as T;
  }

  // Auth
  async verifyPrivy(accessToken: string) {
    return this.request<{
      tokens: { accessToken: string; refreshToken: string; expiresIn: number };
      user: User;
    }>('/auth/privy', {
      method: 'POST',
      body: { privyToken: accessToken },
    });
  }

  async updateProfile(data: { displayName?: string }) {
    const res = await this.request<{ user: User }>('/auth/me', {
      method: 'PATCH',
      body: data,
    });
    return res.user;
  }

  // Public profile
  async getUserProfile(userId: string) {
    return this.request<PublicProfile>(`/auth/users/${userId}`);
  }

  // Notification Preferences
  async getNotificationPreferences() {
    return this.request<NotificationPreferences>('/notifications/preferences');
  }

  async updateNotificationPreferences(data: Partial<NotificationPreferences>) {
    return this.request<NotificationPreferences>('/notifications/preferences', {
      method: 'PATCH',
      body: data,
    });
  }

  // Wagers
  async getWagers(params?: WagerFilters) {
    if (!params) return this.request<Wager[]>('/wagers');
    // Filter out undefined/empty values to avoid sending ?gameCode=undefined
    const clean = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v != null && v !== '')
    );
    const query = Object.keys(clean).length ? `?${new URLSearchParams(clean)}` : '';
    return this.request<Wager[]>(`/wagers${query}`);
  }

  async getMyWagers(status?: string) {
    const query = status ? `?status=${encodeURIComponent(status)}` : '';
    return this.request<Wager[]>(`/wagers/me/wagers${query}`);
  }

  async getWager(id: string) {
    return this.request<Wager>(`/wagers/${id}`);
  }

  async getWagerByCode(code: string) {
    return this.request<Wager>(`/wagers/join/${code}`);
  }

  async createWager(data: CreateWagerInput) {
    return this.request<Wager>('/wagers', { method: 'POST', body: data });
  }

  async acceptWager(id: string) {
    return this.request<Wager>(`/wagers/${id}/accept`, { method: 'POST' });
  }

  async fundWager(id: string, txHash: string) {
    return this.request<Wager>(`/wagers/${id}/fund`, { method: 'POST', body: { txHash } });
  }

  async cancelWager(id: string, reason?: string) {
    return this.request<Wager>(`/wagers/${id}/cancel`, { method: 'POST', body: { reason } });
  }

  async recordCollection(wagerId: string, txHash: string) {
    return this.request<{ message: string }>('/settlement/collect', {
      method: 'POST',
      body: { wagerId, txHash },
    });
  }

  // Game Accounts
  async getAvailableGames() {
    const res = await this.request<{ games: AvailableGame[] }>('/game-accounts/games');
    return res.games;
  }

  async getGameAccounts() {
    const res = await this.request<{ accounts: GameAccount[] }>('/game-accounts');
    return res.accounts;
  }

  async linkGameAccount(gameCode: string, gameUserId: string, gameUsername?: string) {
    const res = await this.request<{ account: GameAccount }>('/game-accounts', {
      method: 'POST',
      body: { gameCode, gameUserId, gameUsername },
    });
    return res.account;
  }

  async unlinkGameAccount(accountId: string) {
    return this.request<void>(`/game-accounts/${accountId}`, { method: 'DELETE' });
  }

  async getGamesWithVerificationInfo() {
    const res = await this.request<{ games: GameWithVerificationInfo[] }>('/game-accounts/games/verification-info');
    return res.games;
  }

  async initiateMatchVerification(gameCode: string, gameUserId: string) {
    const res = await this.request<{ session: MatchVerificationSession }>('/game-accounts/verify/initiate', {
      method: 'POST',
      body: { gameCode, gameUserId },
    });
    return res.session;
  }

  async confirmMatchVerification(sessionId: string) {
    const res = await this.request<{ account: GameAccount }>('/game-accounts/verify/confirm', {
      method: 'POST',
      body: { sessionId },
    });
    return res.account;
  }

  async initiateProfileCodeVerification(gameCode: string, gameUserId: string) {
    const res = await this.request<{ session: ProfileCodeVerificationSession }>('/game-accounts/verify/profile-code/initiate', {
      method: 'POST',
      body: { gameCode, gameUserId },
    });
    return res.session;
  }

  async confirmProfileCodeVerification(sessionId: string) {
    const res = await this.request<{ account: GameAccount }>('/game-accounts/verify/profile-code/confirm', {
      method: 'POST',
      body: { sessionId },
    });
    return res.account;
  }

  async startOAuth(provider: string, game?: string) {
    const res = await this.request<{ authUrl: string }>('/game-accounts/oauth/start', {
      method: 'POST',
      body: { provider, game },
    });
    return res.authUrl;
  }

  // Notifications
  async getNotifications() {
    return this.request<Notification[]>('/notifications');
  }

  async getUnreadNotificationCount() {
    const res = await this.request<{ count: number }>('/notifications/unread-count');
    return res.count;
  }

  async markNotificationRead(id: string) {
    return this.request<void>(`/notifications/${id}/read`, { method: 'POST' });
  }

  async markAllNotificationsRead() {
    return this.request<{ markedAsRead: number }>('/notifications/read-all', { method: 'POST' });
  }

  // Lobbies
  async getAllLobbies() {
    return this.request<LobbyInfo[]>('/lobbies');
  }

  async getLobby(gameCode: string, options?: { page?: number; perPage?: number }) {
    const params = new URLSearchParams();
    if (options?.page) params.set('page', String(options.page));
    if (options?.perPage) params.set('perPage', String(options.perPage));
    const query = params.toString() ? `?${params}` : '';
    return this.request<Lobby>(`/lobbies/${gameCode}${query}`);
  }

  async joinLobby(gameCode: string) {
    return this.request<LobbyMember>(`/lobbies/${gameCode}/join`, { method: 'POST' });
  }

  async leaveLobby(gameCode: string) {
    return this.request<{ message: string }>(`/lobbies/${gameCode}/leave`, { method: 'POST' });
  }

  // Challenges
  async getIncomingChallenges() {
    return this.request<ChallengeWithUsers[]>('/challenges/incoming');
  }

  async getOutgoingChallenges() {
    return this.request<ChallengeWithUsers[]>('/challenges/outgoing');
  }

  async sendChallenge(data: SendChallengeInput) {
    return this.request<ChallengeWithUsers>('/challenges', { method: 'POST', body: data });
  }

  async acceptChallenge(id: string) {
    return this.request<ChallengeWithUsers>(`/challenges/${id}/accept`, { method: 'POST' });
  }

  async declineChallenge(id: string) {
    return this.request<ChallengeWithUsers>(`/challenges/${id}/decline`, { method: 'POST' });
  }

  async cancelChallenge(id: string) {
    return this.request<ChallengeWithUsers>(`/challenges/${id}/cancel`, { method: 'POST' });
  }

  // Admin - Settlement
  async getStuckWagers() {
    const res = await this.request<{ wagers: StuckWager[]; count: number }>('/settlement/admin/stuck');
    return res;
  }

  async forceSettle(wagerId: string, winnerId: string | null, dbOnly: boolean = false) {
    return this.request<{ action: string; txHash?: string }>('/settlement/admin/force-settle', {
      method: 'POST',
      body: { wagerId, winnerId, dbOnly },
    });
  }

  // Admin - Wagers (all wagers regardless of user)
  async adminGetAllWagers(status?: string) {
    const query = status ? `?status=${encodeURIComponent(status)}` : '';
    return this.request<Wager[]>(`/wagers${query}`);
  }

  // Friends
  async getFriends(): Promise<FriendResponse[]> {
    return this.request<FriendResponse[]>('/friends');
  }

  async getFriendRequests(): Promise<FriendRequestResponse[]> {
    return this.request<FriendRequestResponse[]>('/friends/requests');
  }

  async getSentFriendRequests(): Promise<SentFriendRequestResponse[]> {
    return this.request<SentFriendRequestResponse[]>('/friends/requests/sent');
  }

  async sendFriendRequest(addresseeId: string): Promise<void> {
    await this.request('/friends/request', {
      method: 'POST',
      body: { addresseeId },
    });
  }

  async acceptFriendRequest(friendshipId: string): Promise<void> {
    await this.request(`/friends/${friendshipId}/accept`, { method: 'POST' });
  }

  async declineFriendRequest(friendshipId: string): Promise<void> {
    await this.request(`/friends/${friendshipId}/decline`, { method: 'POST' });
  }

  async cancelFriendRequest(friendshipId: string): Promise<void> {
    await this.request(`/friends/${friendshipId}/cancel`, { method: 'POST' });
  }

  async removeFriend(friendshipId: string): Promise<void> {
    await this.request(`/friends/${friendshipId}`, { method: 'DELETE' });
  }

  async searchUsers(query: string): Promise<UserSearchResult[]> {
    return this.request<UserSearchResult[]>(`/friends/search?q=${encodeURIComponent(query)}`);
  }

  // Messages
  async getConversations(): Promise<ConversationResponse[]> {
    return this.request<ConversationResponse[]>('/messages/conversations');
  }

  async getMessages(friendId: string, page?: number): Promise<MessageResponse[]> {
    const params = page ? `?page=${page}` : '';
    return this.request<MessageResponse[]>(`/messages/${friendId}${params}`);
  }

  async sendMessage(friendId: string, content: string): Promise<MessageResponse> {
    return this.request<MessageResponse>(`/messages/${friendId}`, {
      method: 'POST',
      body: { content },
    });
  }

  async markMessagesAsRead(friendId: string): Promise<void> {
    await this.request<void>(`/messages/${friendId}/read`, { method: 'POST' });
  }

  // Waitlist
  async redeemInviteCode(code: string): Promise<{ user: { id: string; accessGranted: boolean } }> {
    return this.request<{ user: { id: string; accessGranted: boolean } }>('/waitlist/redeem', {
      method: 'POST',
      body: { code },
    });
  }

  async joinWaitlist(email: string, xHandle?: string, turnstileToken?: string): Promise<{ id: string }> {
    return this.request<{ id: string }>('/waitlist/join', {
      method: 'POST',
      body: { email, xHandle, turnstileToken },
    });
  }

  // Waitlist (admin)
  async getWaitlistEntries(params?: { status?: string; page?: number; limit?: number }) {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    const query = qs.toString() ? `?${qs}` : '';
    return this.request<WaitlistEntry[]>(`/waitlist/entries${query}`);
  }

  async generateInviteCode(email?: string) {
    return this.request<{ code: string; id: string }>('/waitlist/invite', {
      method: 'POST',
      body: email ? { email } : {},
    });
  }

  async getInviteCodes(params?: { page?: number; limit?: number }) {
    const qs = new URLSearchParams();
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    const query = qs.toString() ? `?${qs}` : '';
    return this.request<InviteCodeEntry[]>(`/waitlist/invites${query}`);
  }

  async grantUserAccess(email: string) {
    return this.request<{ message: string }>('/waitlist/grant-access', {
      method: 'POST',
      body: { email },
    });
  }
}

export const api = new ApiClient();

// Types
export interface User {
  id: string;
  email: string;
  displayName: string;
  avatarUrl?: string;
  walletAddress?: string | null;
  role?: string;
  accessGranted?: boolean;
  createdAt?: string;
}

export interface Wager {
  id: string;
  gameCode: string;
  wagerType: string;
  amount: string;
  currency: string;
  status: string;
  visibility: string;
  challengeCode?: string;
  creator: User;
  opponent?: User;
  winner?: User;
  createdAt: string;
  expiresAt?: string;
  // Escrow fields
  escrowWagerId?: string;
  escrowContractAddress?: string;
  escrowInitTxHash?: string;
  creatorFunded?: boolean;
  opponentFunded?: boolean;
  fundingDeadline?: string;
  matchDeadline?: string;
  platformFeePercent?: string;
}

export interface GameAccount {
  id: string;
  gameCode: string;
  gameUserId: string;
  gameUsername: string;
  username?: string;
  avatarUrl?: string;
  rating?: number;
  rank?: string;
  verified: boolean;
  profileUrl?: string;
  createdAt: string;
}

export interface AvailableGame {
  gameCode: string;
  displayName: string;
  hasApiKey: boolean;
}

export type VerificationMethod = 'api_lookup' | 'oauth' | 'match_verification' | 'in_game_code' | 'manual' | 'none';

export interface GameWithVerificationInfo {
  gameCode: string;
  displayName: string;
  hasApiKey: boolean;
  verificationMethod: VerificationMethod;
  oauthProvider?: 'riot' | 'epic' | 'steam' | 'faceit';
  oauthGameParam?: string;
  inputLabel?: string;
  inputPlaceholder?: string;
}

export interface MatchVerificationSession {
  id: string;
  userId: string;
  gameCode: string;
  gameUserId: string;
  gameUsername: string;
  initiatedAt: number;
  expiresAt: number;
}

export interface ProfileCodeVerificationSession {
  id: string;
  userId: string;
  gameCode: string;
  gameUserId: string;
  gameUsername: string;
  code: string;
  profileField: string;
  initiatedAt: number;
  expiresAt: number;
}

export interface Notification {
  id: string;
  type: string;
  title: string;
  body?: string;
  readAt?: string;
  createdAt: string;
}

export interface NotificationPreferences {
  inApp: boolean;
  pushEnabled: boolean;
  emailEnabled: boolean;
  wagerUpdates: string[];
  challenges: string[];
  securityAlerts: string[];
}

export interface WagerFilters {
  gameCode?: string;
  status?: string;
  minAmount?: string;
  maxAmount?: string;
}

export interface CreateWagerInput {
  gameCode: string;
  wagerType: string;
  wagerTarget?: number;
  amount: string;
  visibility: 'public' | 'private';
  targetOpponentId?: string;
}

// Lobby types
export type PresenceStatus = 'online' | 'away' | 'looking_for_match' | 'in_match' | 'offline';

export interface LobbyMember {
  id: string;
  displayName: string;
  avatarUrl: string | null;
  presence: {
    status: PresenceStatus;
    lastSeenAt: string;
  };
  gameAccount: {
    gameUserId: string;
    username: string;
    rating: number | null;
    rank: string | null;
  };
  stats?: {
    totalWagers: number;
    winRate: number;
  };
}

export interface Lobby {
  gameCode: string;
  gameName: string;
  onlineCount: number;
  memberCount: number;
  members: LobbyMember[];
}

export interface LobbyInfo {
  gameCode: string;
  gameName: string;
  onlineCount: number;
  memberCount: number;
}

// Challenge types
export type ChallengeStatus = 'pending' | 'countered' | 'accepted' | 'declined' | 'expired' | 'cancelled';

export interface ChallengeWithUsers {
  id: string;
  challengerId: string;
  challengedId: string;
  gameCode: string;
  wagerType: string;
  amount: string;
  currency: string;
  gameParameters?: Record<string, unknown>;
  challengeType: 'quick' | 'custom';
  message?: string;
  status: ChallengeStatus;
  counterCount: number;
  lastActionBy?: string;
  wagerId?: string;
  expiresAt: string;
  respondedAt?: string;
  createdAt: string;
  updatedAt: string;
  challenger: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    gameAccount: {
      username: string;
      rating: number | null;
      rank: string | null;
    } | null;
  };
  challenged: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    gameAccount: {
      username: string;
      rating: number | null;
      rank: string | null;
    } | null;
  };
}

export interface SendChallengeInput {
  challengedId: string;
  gameCode: string;
  wagerType?: string;
  amount: string;
  currency?: string;
  gameParameters?: Record<string, unknown>;
  challengeType?: 'quick' | 'custom';
  message?: string;
  expiresInMinutes?: number;
}

// Friend types
export interface FriendResponse {
  id: string;
  displayName: string;
  avatarUrl: string | null;
  status: string;
  friendshipId: string;
  since: string;
}

export interface FriendRequestResponse {
  id: string;
  requester: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
  };
  createdAt: string;
}

export interface SentFriendRequestResponse {
  id: string;
  addressee: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
  };
  createdAt: string;
}

export interface UserSearchResult {
  id: string;
  displayName: string;
  avatarUrl: string | null;
  isFriend: boolean;
  hasPendingRequest: boolean;
}

// Message types
export type MessageType = 'text' | 'challenge_sent' | 'challenge_accepted' | 'challenge_declined' | 'challenge_countered' | 'challenge_expired';

export interface ChallengeMessageMetadata {
  challengeId: string;
  gameCode: string;
  amount: string;
  currency: string;
  status: string;
  wagerId?: string;
  wagerType?: string;
}

export interface ConversationResponse {
  friendId: string;
  friendName: string;
  friendAvatar: string | null;
  lastMessage: {
    content: string;
    senderId: string;
    createdAt: string;
    type?: string;
  } | null;
  unreadCount: number;
}

export interface MessageResponse {
  id: string;
  senderId: string;
  senderName: string;
  senderAvatar: string | null;
  content: string;
  type: MessageType;
  metadata: ChallengeMessageMetadata | null;
  readAt: string | null;
  createdAt: string;
}

// Public profile types
export interface PublicProfile {
  user: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    createdAt: string;
  };
  gameAccounts: {
    id: string;
    gameCode: string;
    username: string;
    avatarUrl: string | null;
    rating: number | null;
    rank: string | null;
  }[];
  stats: {
    totalWagers: number;
    wins: number;
    losses: number;
    draws: number;
    winRate: number;
    totalWagered: number;
    totalWon: number;
    netProfit: number;
  };
  recentWagers: {
    id: string;
    gameCode: string;
    amount: string;
    currency: string;
    status: string;
    createdAt: string;
    creator: { id: string; displayName: string; avatarUrl: string | null };
    opponent: { id: string; displayName: string; avatarUrl: string | null } | null;
    winner: { id: string; displayName: string } | null;
  }[];
}

// Admin types
export interface StuckWager {
  id: string;
  status: string;
  gameCode: string;
  amount: string;
  creatorEmail: string;
  opponentEmail: string | null;
  escrowWagerId: string | null;
  winnerId: string | null;
  creatorFunded: boolean;
  opponentFunded: boolean;
  onChainStatus: string | null;
  onChainPlayer1Funded: boolean | null;
  onChainPlayer2Funded: boolean | null;
  createdAt: string;
  lockedAt: string | null;
  matchDeadline: string | null;
}

// Waitlist types
export interface WaitlistEntry {
  id: string;
  email: string;
  xHandle: string | null;
  status: string;
  createdAt: string;
}

export interface InviteCodeEntry {
  id: string;
  code: string;
  email: string | null;
  createdBy: string;
  usedBy: string | null;
  usedAt: string | null;
  createdAt: string;
  expiresAt: string | null;
}
